import React, { Component } from "react";
import { computed } from "mobx";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Form, Loader, Select } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import CreditCardStore from "~/stores/Credit/CreditCardStore";
import CreditHelper from "~/utils/CreditHelper";
import FormStore from "~/stores/FormStore";
import ProviderStore from "~/stores/Provider/ProviderStore";
import ProductStore from "~/stores/ProductStore";
import CustomerStore from "~/stores/CustomerStore";

@observer
class BillingPreferencesForm extends Component {
  @computed
  get lowBalanceThresholdOptions() {
    const { t, publishTourProductStore } = this.props;

    if (!publishTourProductStore) {
      return [];
    }

    const { product } = publishTourProductStore;

    if (!product) {
      return [];
    }

    const { discountedCreditCost } = product;

    return CreditHelper.getCreditOptions(
      discountedCreditCost,
      [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 75, 100],
      t("disabled"),
    );
  }

  @computed
  get lowBalanceAutomaticReorderQuantityOptions() {
    const { t, publishTourProductStore } = this.props;

    if (!publishTourProductStore) {
      return [];
    }

    const { product } = publishTourProductStore;

    if (!product) {
      return [];
    }

    const { discountedCreditCost } = product;

    return CreditHelper.getCreditOptions(
      discountedCreditCost,
      [10, 15, 20, 25, 30, 75, 100, 300, 1000, 2000],
      t("billingCentral:dontAutomaticallyPurchaseCredits"),
      true,
    );
  }

  @computed
  get creditCardOptions() {
    const { t, creditCardStore } = this.props;
    const { creditCards } = creditCardStore;
    const creditCardOptions = [{ key: "noValue", value: "", text: `(${t("none")})` }];

    creditCards.forEach(creditCard => {
      creditCardOptions.push({
        key: creditCard.creditCardId,
        value: creditCard.creditCardId,
        text: `${creditCard.cardName} ${creditCard.maskedCardNumber}`,
      });
    });

    return creditCardOptions;
  }

  render() {
    const { creditCardStore, formStore, actualUserStore, publishTourProductStore, t } = this.props;
    const { model, setProperty, getAggregatedError } = formStore;
    const { isLoading: creditCardLoading } = creditCardStore;
    const { isLoading: actualUserLoading, errorMessages: actualUserErrorMessages } = actualUserStore;

    let publishTourProductLoading = false;

    if (publishTourProductStore) {
      publishTourProductLoading = publishTourProductStore.isLoading;
    }

    const validationRules = {};
    const validation = getValidationErrors(validationRules, model);

    if (actualUserLoading || creditCardLoading || publishTourProductLoading) {
      return (
        <div className="side-page-loader">
          <Loader active inline="centered" />
        </div>
      );
    }

    return (
      <Form className="side-page-form-wrap">
        <div className="side-page-input">
          <h4>{t("billingCentral:defaultCreditCard")}</h4>
          <Form.Field
            control={Select}
            name="defaultCreditCardId"
            value={model.defaultCreditCardId}
            options={this.creditCardOptions}
            onChange={(_event, { value }) => setProperty("defaultCreditCardId", value)}
            error={getAggregatedError("defaultCreditCardId", validation, actualUserErrorMessages)}
            placeholder={`${t("none")}`}
          />
        </div>

        {actualUserStore instanceof ProviderStore && (
          <div className="side-page-input">
            <h4 className="with-tagline">{t("billingCentral:lowBalanceThreshold")}</h4>
            <label className="side-page-tagline">{t("billingCentral:lowBalanceThresholdTagline")}</label>
            <Form.Field
              control={Select}
              name="lowCreditBalanceNotificationThreshold"
              value={model.lowCreditBalanceNotificationThreshold}
              options={this.lowBalanceThresholdOptions}
              onChange={(_event, { value }) => setProperty("lowCreditBalanceNotificationThreshold", value)}
              error={getAggregatedError("lowCreditBalanceNotificationThreshold", validation, actualUserErrorMessages)}
              placeholder={t("disabled")}
            />
          </div>
        )}

        {actualUserStore instanceof ProviderStore && (
          <div className="side-page-input">
            <h4 className="with-tagline">{t("billingCentral:lowBalanceAutomaticReorderQuantity")}</h4>
            <label className="side-page-tagline">{t("billingCentral:lowBalanceAutomaticReorderQuantityTagline")}</label>
            <Form.Field
              control={Select}
              name="lowCreditBalanceThreshholdAutoPurchaseAmount"
              value={model.lowCreditBalanceThreshholdAutoPurchaseAmount}
              options={this.lowBalanceAutomaticReorderQuantityOptions}
              onChange={(_event, { value }) => setProperty("lowCreditBalanceThreshholdAutoPurchaseAmount", value)}
              error={getAggregatedError(
                "lowCreditBalanceThreshholdAutoPurchaseAmount",
                validation,
                actualUserErrorMessages,
              )}
              placeholder={t("billingCentral:dontAutomaticallyPurchaseCredits")}
            />
          </div>
        )}
      </Form>
    );
  }
}

BillingPreferencesForm.propTypes = {
  creditCardStore: PropTypes.instanceOf(CreditCardStore),
  formStore: PropTypes.instanceOf(FormStore),
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  publishTourProductStore: PropTypes.instanceOf(ProductStore),
  t: PropTypes.func.isRequired,
};

export default withTranslation()(BillingPreferencesForm);
